import { useNavigate, useSearch as useRouterSearch } from '@tanstack/react-router';
import { Trans } from '@lingui/react/macro';
import type { ReactNode } from 'react';
import type { SearchRouteSearch } from '../../services/search';

type SearchSort = SearchRouteSearch['sort'];

interface SearchSortOptionsProps {
  /** Called after a sort is applied, so the caller can dismiss the popover. */
  closePopover?: () => void;
}

const SEARCH_SORTS: Array<{ value: SearchSort; label: ReactNode }> = [
  { value: 'relevance', label: <Trans>Relevance</Trans> },
  { value: 'newest', label: <Trans>Newest First</Trans> },
  { value: 'oldest', label: <Trans>Oldest First</Trans> },
];

/**
 * Sort control for the search results. Unlike the newest/oldest toggle used by
 * the other sections (a stored preference), search sort lives in the `?sort=`
 * URL param, so this reads and updates the route search directly. Changing sort
 * resets to page 1 (page is dropped from the URL).
 */
function SearchSortOptions({ closePopover }: SearchSortOptionsProps) {
  const navigate = useNavigate();
  const search = useRouterSearch({ strict: false }) as Partial<SearchRouteSearch>;
  const currentSort: SearchSort = search.sort ?? 'relevance';

  const setSort = (sort: SearchSort) => {
    if (sort === currentSort) return;
    navigate({
      to: '/search',
      search: {
        q: search.q ?? '',
        site: search.site ?? '',
        author: search.author ?? '',
        sort,
      },
      replace: true,
    });
    closePopover?.();
  };

  return (
    <>
      <div className="menu-header">
        <Trans>Sort</Trans>
      </div>

      <div className="flex flex-col">
        {SEARCH_SORTS.map(({ value, label }) => (
          <button
            key={value}
            onClick={() => setSort(value)}
            className="group menu-item justify-between"
          >
            <span>{label}</span>
            {currentSort === value && (
              <svg
                className="text-copy-secondary group-hover:text-copy-primary h-2 w-2.5 fill-none stroke-current stroke-2"
                viewBox="0 0 9 8"
              >
                <path d="M1 4.31034L3.69231 7L8 1" />
              </svg>
            )}
          </button>
        ))}
      </div>
    </>
  );
}

export default SearchSortOptions;
